import React, { useState, useEffect, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import styles from "./index.module.css";
import { useNavigate } from "react-router";
import PuffLoader from "react-spinners/PuffLoader";

const StackedBarChart = (props) => {
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const updateChartSize = () => {
      if (chartContainerRef.current) {
        // For terminals, use container width, for stores use calculated width
        const minWidth =
          props.type === "terminal"
            ? chartContainerRef.current.offsetWidth
            : Math.max(
                props.data.length * 150, // Increased from 100px to 150px per bar
                chartContainerRef.current.offsetWidth
              );
        setChartWidth(minWidth);
        setChartHeight(chartContainerRef.current.offsetHeight);
      }
    };

    updateChartSize();

    window.addEventListener("resize", updateChartSize);
    return () => window.removeEventListener("resize", updateChartSize);
  }, [props.data, props.type]);

  // Handler function for bar clicks
  const handleBarClick = (data, index, label) => {
    if (props.url == "/dashboard/fnb-products") {
      if (props.type === "terminal") {
        navigate(
          `${props?.url}?label=${label
            ?.replace("_count", "")
            ?.toLowerCase()}&terminal=${data?.[props?.type]?.toLowerCase()}`
        );
      } else {
        navigate(
          `${props?.url}?label=${label
            ?.replace("_count", "")
            ?.toLowerCase()}&store=${data?.[
            props?.type
          ]?.toLowerCase()} (${data?.terminal?.toLowerCase()})`
        );
      }
    } else {
      navigate(
        `${props?.url}?pricerange=${label
          ?.replace("_count", "")
          ?.toLowerCase()}&${props?.type}=${data?.[props?.type]?.toLowerCase()}`
      );
    }
  };

  return props.data.length > 0 ? (
    <div
      className={`${styles["chart-container"]} ${
        props.type === "terminal" ? styles["terminal-chart"] : ""
      }`}
      ref={chartContainerRef}
    >
      <div className={styles["chart-wrapper"]}>
        {chartWidth > 0 && (
          <BarChart
            width={chartWidth}
            height={chartHeight}
            data={props?.data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: props.type === "terminal" ? 10 : 20,
            }}
            barSize={props.type === "terminal" ? 50 : 60}
            barGap={props.type === "terminal" ? 0 : 20}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={props?.type}
              style={{
                fontSize: props.type === "terminal" ? "10px" : "8px",
                fontWeight: "bold",
              }}
              height={props.type === "terminal" ? 30 : 60}
              tick={{ angle: 0 }}
              interval={0}
              tickFormatter={(value, index) => {
                if (props.type === "store") {
                  const dataPoint = props.data[index];
                  return `${value}\n(${dataPoint.terminal})`;
                }
                return value;
              }}
            />
            <YAxis style={{ fontSize: "10px", fontWeight: "bold" }} />
            <Tooltip
              contentStyle={{
                backgroundColor: "#333",
                color: "#fff",
                fontSize: "1.4rem",
              }}
              labelStyle={{ color: "#fff" }}
            />

            <Bar
              dataKey={props?.blueLabel}
              stackId="a"
              fill="#a3d9c5"
              onClick={(data, index) =>
                handleBarClick(data, index, props?.blueLabel)
              }
            >
              <LabelList
                dataKey={props?.blueLabel}
                position="middle"
                fill="#000"
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  pointerEvents: "none",
                }}
                formatter={(value) => (Number(value) !== 0 ? value : "")}
              />
            </Bar>
            <Bar
              dataKey={props?.greyLabel}
              stackId="a"
              fill="#ffffb7"
              onClick={(data, index) =>
                handleBarClick(data, index, props?.greyLabel)
              }
            >
              <LabelList
                dataKey={props?.greyLabel}
                position="middle"
                fill="#000"
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  pointerEvents: "none",
                }}
                formatter={(value) => (Number(value) !== 0 ? value : "")}
              />
            </Bar>
            <Bar
              dataKey={props?.greenLabel}
              stackId="a"
              fill="#ff9999"
              onClick={(data, index) =>
                handleBarClick(data, index, props?.greenLabel)
              }
            >
              <LabelList
                dataKey={props?.greenLabel}
                position="middle"
                fill="#000"
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  pointerEvents: "none",
                }}
                formatter={(value) => (Number(value) !== 0 ? value : "")}
              />
            </Bar>
          </BarChart>
        )}
      </div>
    </div>
  ) : (
    <div className="loader-container">
      <PuffLoader />
    </div>
  );
};

export default StackedBarChart;
